// A drag that STARTS on a mark (#304).
//
// Its own file because `src/wheel/camera.ts` is the camera's rules and this is
// one input's workaround: the layering of Decision 47 leaves the marks as the
// only thing on the overlay that takes a pointer, and a pointer they take is a
// pointer MapLibre never hears.
import { clampCentre } from '../state/clamp.ts';
import type { CameraDeps, Live } from './camera.ts';

/** How far a finger may drift on a mark before it is a drag and not a tap. */
const TAP_SLACK_PX = 8;

/**
 * A drag that STARTS on a mark pans the map (#304).
 *
 * The marks are the only thing on the overlay that takes a pointer — they have
 * to, or a mark could not be tapped — and the overlay around them is
 * `pointer-events: none` so that everything else falls through to MapLibre's
 * canvas. A pointer that goes down on a badge therefore never reaches MapLibre
 * at all, and the map simply did not move: measured at 1 in 3 centre drags on
 * the desktop and on the FIRST drag of nearly every phone run, because „16 ↗"
 * sits on the centre dot at the default lines.
 *
 * So the pan is driven here instead. Past {@link TAP_SLACK_PX} this is a
 * gesture like any other: it sets `gesturing`, so `camera()` stands back; it
 * clamps; and it is finished by the same pointer-up that finishes a canvas
 * drag, through the same `settle`. Below the slack nothing happens at all and
 * the mark gets its tap — one gesture, one result (D-d).
 *
 * Re-dispatching the pointer to the canvas was the other way, and it was
 * rejected: a synthetic `TouchEvent` needs the `Touch` constructor, which is
 * not a promise this app can make on the owner's phone.
 */
export function relayFromMarks(deps: CameraDeps, live: Live) {
  const { map, marks } = deps;
  /** Where the finger went down, and the centre it went down on. */
  let from: { x: number; y: number } | null = null;
  let origin: { lat: number; lon: number } | null = null;

  marks.addEventListener('pointerdown', (e) => {
    if (!deps.offered() || deps.disc() === 0) return;
    const at = map.getCenter();
    from = { x: e.clientX, y: e.clientY };
    origin = { lat: at.lat, lon: at.lng };
  });
  window.addEventListener('pointermove', (e) => {
    if (!from || !origin) return;
    if (Math.hypot(e.clientX - from.x, e.clientY - from.y) <= TAP_SLACK_PX) return;
    live.gesturing = true;
    live.dragged = true;
    if (live.startZoom === null) live.startZoom = map.getZoom();
    // The ORIGINAL centre, re-projected every frame and moved by the whole
    // delta: reading the delta frame by frame would drift, and re-projecting
    // is also what makes the clamp settle rather than fight.
    const pixels = map.project([origin.lon, origin.lat]);
    const to = map.unproject([pixels.x - (e.clientX - from.x), pixels.y - (e.clientY - from.y)]);
    const inside = clampCentre({ lat: to.lat, lon: to.lng });
    live.owning = true;
    map.setCenter([inside.lon, inside.lat]);
    live.owning = false;
  });
  for (const type of ['pointerup', 'pointercancel'] as const) {
    // After `hearPointers`, whose own listener calls `finish`. The ORDER is the
    // guarantee and it holds for a plainer reason than registration: that
    // listener is on the window in the CAPTURE phase and this one is in the
    // bubble phase, and capture always runs first. So the gesture is finished
    // before its start is forgotten.
    window.addEventListener(type, () => {
      from = null;
      origin = null;
    });
  }
}
